/**
 * Metric autocomplete for the calculated-row expression input.
 *
 * Same shape as the chat's `useDocumentMention`: the hook watches the word
 * under the cursor, keeps the match list and the highlighted row, and hands
 * back a NEW input value when a selection is committed — it never touches the
 * DOM itself. What differs is the source: the vocabulary is the loaded table's
 * metrics, already in memory, so there is no debounce, no fetch, and no
 * loading state to render.
 *
 * It exists because the expression takes KEYS and a reader knows LABELS.
 * Typing "total units / occupied units" reads like the metric names on screen
 * and is rejected by the server, which is the one failure a list of available
 * keys printed under the box did not prevent — it asked the user to transcribe
 * `total_units` correctly from a paragraph.
 */

import { useCallback, useMemo, useState } from "react";

export interface KpiMetricOption {
	key: string;
	label: string;
}

export interface KpiSuggestEdit {
	/** The expression with the current word replaced by the metric key. */
	value: string;
	/** Where the caret belongs afterwards — just past the inserted key. */
	cursor: number;
}

export interface KpiMetricSuggestState {
	/** True when a dropdown should be on screen. */
	isActive: boolean;
	results: KpiMetricOption[];
	highlightedIndex: number;
	/** Call on every change of the input, with the caret position. */
	handleChange: (value: string, cursor: number) => void;
	/**
	 * Call from the input's `onKeyDown`. Returns an edit when the key committed
	 * a selection, `"handled"` when the key was consumed (navigation, dismiss),
	 * and null when the key belongs to the input — operators, parens, digits and
	 * ordinary letters all fall through here.
	 */
	handleKeyDown: (
		event: React.KeyboardEvent<HTMLInputElement>,
		value: string,
		cursor: number,
	) => KpiSuggestEdit | "handled" | null;
	/** Commit a row by mouse. */
	selectMetric: (
		option: KpiMetricOption,
		value: string,
		cursor: number,
	) => KpiSuggestEdit;
	dismiss: () => void;
}

/** Shortest word worth matching on — one letter matches nearly everything. */
const MIN_QUERY = 2;
/** Enough to choose from without turning the dropdown into the paragraph it replaced. */
const RESULT_LIMIT = 8;

/**
 * The identifier being typed: the run of word characters ending at the caret.
 *
 * Operators, spaces and parens end a word, which is what lets `noi/` or
 * `(units` start a fresh match without the user clearing anything.
 */
export function currentWord(
	value: string,
	cursor: number,
): { text: string; start: number } {
	const upToCaret = value.slice(0, cursor);
	const match = /[A-Za-z0-9_]+$/.exec(upToCaret);
	if (!match) return { text: "", start: cursor };
	return { text: match[0], start: cursor - match[0].length };
}

/** Does this word deserve a dropdown? Bare numbers never do. */
function isQueryable(word: string): boolean {
	return word.length >= MIN_QUERY && /[A-Za-z_]/.test(word);
}

export function matchMetrics(
	options: KpiMetricOption[],
	query: string,
): KpiMetricOption[] {
	const q = query.toLowerCase();
	return options
		.filter(
			(option) =>
				option.label.toLowerCase().includes(q) ||
				option.key.toLowerCase().includes(q),
		)
		.slice(0, RESULT_LIMIT);
}

export function useKpiMetricSuggest(
	options: KpiMetricOption[],
): KpiMetricSuggestState {
	const [query, setQuery] = useState("");
	const [dismissed, setDismissed] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(0);

	const results = useMemo(
		() => (isQueryable(query) ? matchMetrics(options, query) : []),
		[options, query],
	);

	const isActive = !dismissed && results.length > 0;

	const handleChange = useCallback((value: string, cursor: number) => {
		const word = currentWord(value, cursor);
		setQuery(word.text);
		setHighlightedIndex(0);
		// Typing re-opens a dismissed dropdown: Escape means "not this word",
		// not "never again in this field".
		setDismissed(false);
	}, []);

	const dismiss = useCallback(() => setDismissed(true), []);

	const selectMetric = useCallback(
		(option: KpiMetricOption, value: string, cursor: number): KpiSuggestEdit => {
			const word = currentWord(value, cursor);
			const next =
				value.slice(0, word.start) + option.key + value.slice(cursor);
			setQuery("");
			setHighlightedIndex(0);
			return { value: next, cursor: word.start + option.key.length };
		},
		[],
	);

	const handleKeyDown = useCallback(
		(
			event: React.KeyboardEvent<HTMLInputElement>,
			value: string,
			cursor: number,
		): KpiSuggestEdit | "handled" | null => {
			if (!isActive) return null;
			switch (event.key) {
				case "ArrowDown":
					event.preventDefault();
					setHighlightedIndex((prev) =>
						prev < results.length - 1 ? prev + 1 : 0,
					);
					return "handled";
				case "ArrowUp":
					event.preventDefault();
					setHighlightedIndex((prev) =>
						prev > 0 ? prev - 1 : results.length - 1,
					);
					return "handled";
				case "Enter":
				case "Tab":
					event.preventDefault();
					// Stopped here as well as prevented: Enter would otherwise submit
					// the form under it, and Tab is caught by the dialog's focus trap.
					event.stopPropagation();
					return selectMetric(results[highlightedIndex], value, cursor);
				case "Escape":
					event.preventDefault();
					// The dialog closes on Escape at the window; with a dropdown open
					// the first Escape belongs to the dropdown.
					event.stopPropagation();
					dismiss();
					return "handled";
				default:
					return null;
			}
		},
		[dismiss, highlightedIndex, isActive, results, selectMetric],
	);

	return {
		isActive,
		results,
		highlightedIndex,
		handleChange,
		handleKeyDown,
		selectMetric,
		dismiss,
	};
}
